import {
  type Banner,
  type Customer,
  getAnnouncementBanner,
  getFeaturedCustomers,
  getPage,
  getSimpleIntegrations,
  getDefaultLogos,
  getDefaultMobileLogos,
  type IntegrationSimple,
  type Page,
  getSimpleCaseStudies,
  type CaseStudySimple,
  type IntegrationCategory,
  type BlogSimple,
  getFeaturedBlogs,
  type EmbeddedWhitepaper,
  getEmbeddedWhitepaper,
} from "lib/sanity";
import { type GetStaticProps } from "next";
import { type PageProps } from "components/page";

type MetaImage = {
  url: string;
  alt: string;
  width: number;
  height: number;
};

export function getPageMetaImage(metaImage?: { src?: string; alt?: string }): MetaImage {
  return {
    url: metaImage?.src ?? "https://hightouch.com/assets/meta.png",
    alt: metaImage?.alt ?? "Hightouch.",
    width: 1200,
    height: 630,
  };
}

export const downloadUrl = (url: string, filename: string) => {
  const a = document.createElement("a");

  a.href = url;
  a.download = filename;

  const clickHandler = () => {
    setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 150);
  };

  a.addEventListener("click", clickHandler, false);
  a.click();
};

export type LandingPageProps = Readonly<{
  noIndex?: boolean;
  headerVariant?: PageProps["headerVariant"];
  page: Page;
  announcementBanner?: Banner;
  defaultLogos: Customer[];
  defaultMobileLogos: Customer[];
  customers: Customer[];
  sources: IntegrationSimple[];
  destinations: IntegrationSimple[];
  caseStudies: CaseStudySimple[];
  blogs: BlogSimple[];
  embeddedWhitepaper?: EmbeddedWhitepaper | null; // eslint-disable-line @typescript-eslint/ban-types
}>;

type LandingPageOptions = {
  /**
   * Set the page to noIndex. Only used when page has a duplicate, ex. ads page.
   * @default false
   */
  noIndex?: boolean;
  /** Define variant for the page header, typically used for ads pages.
   * @default "full"
   */
  headerVariant?: PageProps["headerVariant"];
  /** Load the default set of customer logos from the site settings. */
  loadDefaultLogos?: boolean;
  /** Load the default set of mobile customer logos from the site settings. */
  loadDefaultMobileLogos?: boolean;
  /** Load all source integrations. */
  loadSources?: boolean;
  /** Load all destination integrations. */
  loadDestinations?: "all" | IntegrationCategory;
  /** Load the specified customers by slug. */
  loadCustomers?: string[];
  /** Load case studies, either "all" or for specific customer slugs. */
  loadCaseStudies?: "all" | string[];
  /** Load specific blog slugs. Can be an array of strings or an object with slugs and allowHidden option. */
  loadBlogs?: string[] | { slugs: string[]; allowHidden?: boolean };
  /** Load an embedded whitepaper by slug. */
  loadEmbeddedWhitepaper?: string;
};

/**
 * Use as a pass-through for `getStaticProps` for all of the [landing pages](https://hightouch.sanity.studio/production/structure/marketing;landingPages) from Sanity.
 * Automatically handles redirecting to a 404 if the page slug cannot be loaded.
 *
 * @param slug The relative slug of the page to load, with leading `/`.
 * @param options Used to specify what data needs to be loaded from Sanity for this page.
 */
export const getLandingPageProps = (slug: string, options?: LandingPageOptions): GetStaticProps<LandingPageProps> => {
  return async () => {
    const [
      page,
      announcementBanner,
      defaultLogos,
      defaultMobileLogos,
      customers,
      destinations,
      sources,
      caseStudies,
      blogs,
      embeddedWhitepaper,
    ] = await Promise.all([
      // Every page needs these
      getPage(slug),
      getAnnouncementBanner(),
      // Only load in additional data if it was requested, otherwise just set an empty array
      options?.loadDefaultLogos ? getDefaultLogos() : [],
      options?.loadDefaultMobileLogos ? getDefaultMobileLogos() : [],
      options?.loadCustomers ? getFeaturedCustomers(options.loadCustomers) : [],
      options?.loadDestinations ? getSimpleIntegrations("destination", options.loadDestinations) : [],
      options?.loadSources ? getSimpleIntegrations("source") : [],
      options?.loadCaseStudies
        ? getSimpleCaseStudies(options.loadCaseStudies === "all" ? undefined : options.loadCaseStudies)
        : [],
      options?.loadBlogs
        ? Array.isArray(options.loadBlogs)
          ? getFeaturedBlogs(options.loadBlogs)
          : getFeaturedBlogs(options.loadBlogs.slugs, options.loadBlogs.allowHidden)
        : [],
      options?.loadEmbeddedWhitepaper ? getEmbeddedWhitepaper(options.loadEmbeddedWhitepaper) : null,
    ]);

    if (!page) {
      return {
        notFound: true,
      };
    }

    const noIndex = options?.noIndex ?? false;
    const headerVariant = options?.headerVariant ?? "full";

    return {
      props: {
        noIndex,
        headerVariant,
        page,
        announcementBanner,
        defaultLogos,
        defaultMobileLogos,
        customers,
        destinations,
        sources,
        caseStudies,
        blogs,
        embeddedWhitepaper,
      },
      revalidate: 60,
    };
  };
};

/**
 * Helper function to extract a single customer record out of the set of customers that
 * were loaded from Sanity, to simplify null checking and error handling.
 *
 * The `getFeaturedCustomers` function will already report a client error if the
 * customer slug was missing from Sanity, so we don't need to report on that here
 * and can safely assume the record exists.
 */
export const getSafeCustomer = (loadedCustomers: Customer[], slug: string): Customer => {
  return loadedCustomers.find((c) => c.slug === slug)!;
};

/**
 * Helper function to extract a single case study record out of the set of case studies that
 * were loaded from Sanity, to simplify null checking and error handling.
 *
 * The `getSimpleCaseStudies` function will already report a client error if the
 * case study slug was missing from Sanity, so we don't need to report on that here
 * and can safely assume the record exists.
 */
export const getSafeCaseStudy = (loadedCaseStudies: CaseStudySimple[], slug: string): CaseStudySimple => {
  return loadedCaseStudies.find((c) => c.customer.slug === slug)!;
};
